import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import * as Yup from "yup";
import Modal, {
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../components/modal/Modal";
import TusJsClientWrapper from "../../components/uploads/TusJsClientWrapper";
import { bytesToSize } from "../../stores/utils";
import * as deliveryNotesApi from "../../services/delivery_notes";
import { useAuthStore } from "../../stores/auth";

export default function DeliveryNoteEmailModal({ deliveryNote, onClose }) {
  const { t } = useTranslation();
  const [sentTo, setSentTo] = useState(null);
  const me = useAuthStore((state) => state.me);

  const defaultTo = deliveryNote.customer?.contacts?.[0]?.email || "";
  const defaultSubject = `${t("delivery_notes.delivery_note")} Nr. ${deliveryNote.number}`;

  const initialValues = {
    to: defaultTo,
    subject: defaultSubject,
    message: "",
    attachments: [],
  };

  const validationSchema = Yup.object().shape({
    to: Yup.string()
      .email(t("form.errors.email"))
      .required(t("form.errors.required", { attr: t("common.recipient") })),
    subject: Yup.string().required(
      t("form.errors.required", { attr: t("common.subject") }),
    ),
    message: Yup.string(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    const uploadIds = values.attachments.map((a) => a.url.split("/").pop());
    const result = await deliveryNotesApi.sendEmail({
      id: deliveryNote.id,
      to: values.to,
      subject: values.subject,
      message: values.message,
      upload_ids: uploadIds,
    });
    setSubmitting(false);
    if (!result.error) {
      setSentTo(values.to);
      setTimeout(onClose, 2500);
    }
  };

  return (
    <Modal size="lg">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={true}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, values }) => {
          const MAX_SIZE = 10 * 1024 * 1024;
          const attachmentSize = values.attachments.reduce(
            (sum, f) => sum + f.size,
            0,
          );
          const exceedsLimit = attachmentSize > MAX_SIZE;

          return (
            <Form>
              <ModalHeader>
                <h5 className="modal-title">{t("common.send_email")}</h5>
              </ModalHeader>
              <ModalBody>
                {sentTo ? (
                  <div className="alert alert-success mb-0">
                    <i className="fa fa-check me-2" />
                    {t("common.email_sent")}
                    <div className="mt-2 small">
                      {t("common.recipient")}: {sentTo}
                      <br />
                      {t("common.email_copy_sent_to", { email: me?.email })}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("common.recipient")}
                      </label>
                      <Field
                        type="email"
                        name="to"
                        className={`form-control${
                          errors.to && touched.to ? " is-invalid" : ""
                        }`}
                        disabled={isSubmitting}
                      />
                      <ErrorMessage
                        name="to"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("common.subject")}
                      </label>
                      <Field
                        type="text"
                        name="subject"
                        className={`form-control${
                          errors.subject && touched.subject ? " is-invalid" : ""
                        }`}
                        disabled={isSubmitting}
                      />
                      <ErrorMessage
                        name="subject"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("common.message")}
                      </label>
                      <Field
                        as="textarea"
                        name="message"
                        className="form-control"
                        rows={8}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("common.attachments")}
                      </label>
                      <FieldArray name="attachments">
                        {(arrayHelpers) => (
                          <>
                            <ul className="list-group mb-2">
                              <li className="list-group-item d-flex align-items-center text-muted">
                                <i className="fa fa-file-pdf me-2" />
                                <span>
                                  {`delivery_note_${deliveryNote.number}.pdf`}
                                </span>
                              </li>
                              {values.attachments.map((file, index) => (
                                <li
                                  key={index}
                                  className="list-group-item d-flex align-items-center"
                                >
                                  <i className="fa fa-paperclip me-2" />
                                  <span className="me-2">{file.name}</span>
                                  <small className="text-muted me-auto">
                                    ({bytesToSize(file.size)})
                                  </small>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-default"
                                    onClick={() => arrayHelpers.remove(index)}
                                    disabled={isSubmitting}
                                  >
                                    <i className="fa fa-times" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                            {/* TODO: re-enable additional file attachments */}
                            {false && (
                              <TusJsClientWrapper
                                accept="*/*"
                                hideFinished={true}
                                onSuccess={(upload) =>
                                  arrayHelpers.push(upload)
                                }
                              />
                            )}
                            {exceedsLimit && (
                              <div className="alert alert-danger mt-2 mb-0">
                                {t("common.attachments_too_large")}
                              </div>
                            )}
                          </>
                        )}
                      </FieldArray>
                    </div>
                  </>
                )}
              </ModalBody>
              {!sentTo && (
                <ModalFooter>
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-red"
                    disabled={isSubmitting || exceedsLimit}
                  >
                    {isSubmitting && (
                      <i className="fa fa-spinner fa-spin me-1" />
                    )}
                    {t("common.send")}
                  </button>
                </ModalFooter>
              )}
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}

DeliveryNoteEmailModal.propTypes = {
  deliveryNote: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};
